import { c as _c } from "react/compiler-runtime";
import { type Dispatch, useCallback, useId, useState, type KeyboardEvent, type RefObject, type SetStateAction } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { GroupsRequestParams } from "@/entities/IGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { Mutable, NonNullableNotRequired } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
const MAX_RESULTS = 7;
const DEBOUNCE_MS = 200;
type CreateWaveGroupSearchParams = {
  readonly defaultLabel: string;
  readonly disabled: boolean;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly selectedGroup: ApiGroupFull | null;
  readonly allowClear: boolean;
  readonly onSelect: (group: ApiGroupFull | null) => void;
  readonly wrapperRef: RefObject<HTMLDivElement | null>;
};
type CreateWaveGroupSearchState = {
  readonly selectedGroupKey: string | null;
  readonly inputValue: string;
  readonly searchCriteria: string;
  readonly activeIndex: number;
};
function getSelectedGroupKey(group: ApiGroupFull | null): string | null {
  if (!group) {
    return null;
  }
  return JSON.stringify([group.id, group.name]);
}
function createSearchState({
  selectedGroupKey,
  selectedGroupName
}: {
  readonly selectedGroupKey: string | null;
  readonly selectedGroupName: string;
}): CreateWaveGroupSearchState {
  return {
    selectedGroupKey,
    inputValue: selectedGroupName,
    searchCriteria: selectedGroupName,
    activeIndex: -1
  };
}
function getEffectiveSearchState({
  searchState,
  selectedGroupKey,
  selectedGroupName
}: {
  readonly searchState: CreateWaveGroupSearchState;
  readonly selectedGroupKey: string | null;
  readonly selectedGroupName: string;
}): CreateWaveGroupSearchState {
  if (searchState.selectedGroupKey === selectedGroupKey) {
    return searchState;
  }
  return createSearchState({
    selectedGroupKey,
    selectedGroupName
  });
}
function useCreateWaveGroupSuggestions(t0) {
  const $ = _c(15);
  const {
    debouncedValue,
    disabled,
    isOpen
  } = t0;
  const t1 = debouncedValue || null;
  let t2;
  if ($[0] !== t1) {
    t2 = [QueryKey.GROUPS, {
      group_name: t1
    }];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== debouncedValue) {
    t3 = async () => {
      const params = {};
      if (debouncedValue) {
        params.group_name = debouncedValue;
      }
      return await commonApiFetch({
        endpoint: "groups",
        params
      });
    };
    $[2] = debouncedValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isOpen && !disabled;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      placeholderData: keepPreviousData
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data,
    isFetching
  } = useQuery(t5);
  let t6;
  if ($[8] !== data) {
    t6 = data ?? [];
    $[8] = data;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = t6.slice(0, MAX_RESULTS);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isFetching || $[13] !== t7) {
    t8 = {
      isFetching,
      suggestions: t7
    };
    $[12] = isFetching;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function useCreateWaveGroupKeyboardNavigation(t0) {
  const $ = _c(8);
  const {
    activeIndex,
    closeSearch,
    isOpen,
    onOptionSelect,
    setActiveIndex,
    setIsOpen,
    suggestions
  } = t0;
  let t1;
  if ($[0] !== activeIndex || $[1] !== closeSearch || $[2] !== isOpen || $[3] !== onOptionSelect || $[4] !== setActiveIndex || $[5] !== setIsOpen || $[6] !== suggestions) {
    t1 = event => {
      if (!isOpen) {
        if (event.key === "ArrowDown") {
          setIsOpen(true);
          setActiveIndex(0);
          event.preventDefault();
        }
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= suggestions.length) {
            return suggestions.length ? 0 : -1;
          }
          return nextIndex;
        });
      } else {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setActiveIndex(prev_0 => {
            if (suggestions.length === 0) {
              return -1;
            }
            if (prev_0 <= 0) {
              return suggestions.length - 1;
            }
            return prev_0 - 1;
          });
        } else {
          if (event.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
              event.preventDefault();
              onOptionSelect(suggestions[activeIndex]);
            }
          } else {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              closeSearch();
            }
          }
        }
      }
    };
    $[0] = activeIndex;
    $[1] = closeSearch;
    $[2] = isOpen;
    $[3] = onOptionSelect;
    $[4] = setActiveIndex;
    $[5] = setIsOpen;
    $[6] = suggestions;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function useCreateWaveGroupSearchState(t0) {
  const $ = _c(22);
  const {
    selectedGroupKey,
    selectedGroupName
  } = t0;
  let t1;
  if ($[0] !== selectedGroupKey || $[1] !== selectedGroupName) {
    t1 = () => createSearchState({
      selectedGroupKey,
      selectedGroupName
    });
    $[0] = selectedGroupKey;
    $[1] = selectedGroupName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [searchState, setSearchState] = useState(t1);
  const [debouncedValue, setDebouncedValue] = useState(selectedGroupName);
  let t2;
  if ($[3] !== searchState || $[4] !== selectedGroupKey || $[5] !== selectedGroupName) {
    t2 = getEffectiveSearchState({
      searchState,
      selectedGroupKey,
      selectedGroupName
    });
    $[3] = searchState;
    $[4] = selectedGroupKey;
    $[5] = selectedGroupName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const effectiveSearchState = t2;
  const {
    activeIndex,
    inputValue,
    searchCriteria
  } = effectiveSearchState;
  let t3;
  if ($[7] !== selectedGroupKey || $[8] !== selectedGroupName) {
    t3 = getNextState => {
      setSearchState(current => {
        const effectiveCurrent = getEffectiveSearchState({
          searchState: current,
          selectedGroupKey,
          selectedGroupName
        });
        return getNextState(effectiveCurrent);
      });
    };
    $[7] = selectedGroupKey;
    $[8] = selectedGroupName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const updateSearchState = t3;
  let t4;
  if ($[10] !== updateSearchState) {
    t4 = nextActiveIndex => {
      updateSearchState(current_0 => ({
        ...current_0,
        activeIndex: typeof nextActiveIndex === "function" ? nextActiveIndex(current_0.activeIndex) : nextActiveIndex
      }));
    };
    $[10] = updateSearchState;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const setActiveIndex = t4;
  let t5;
  if ($[12] !== searchCriteria) {
    t5 = () => {
      setDebouncedValue(searchCriteria.trim());
    };
    $[12] = searchCriteria;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== searchCriteria) {
    t6 = [searchCriteria];
    $[14] = searchCriteria;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useDebounce(t5, DEBOUNCE_MS, t6);
  let t7;
  if ($[16] !== activeIndex || $[17] !== debouncedValue || $[18] !== inputValue || $[19] !== searchCriteria || $[20] !== setActiveIndex) {
    t7 = {
      activeIndex,
      debouncedValue,
      inputValue,
      searchCriteria,
      setActiveIndex,
      setSearchState
    };
    $[16] = activeIndex;
    $[17] = debouncedValue;
    $[18] = inputValue;
    $[19] = searchCriteria;
    $[20] = setActiveIndex;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
export function useCreateWaveGroupSearch(t0) {
  const $ = _c(67);
  const {
    defaultLabel,
    disabled,
    inputRef,
    selectedGroup,
    allowClear,
    onSelect,
    wrapperRef
  } = t0;
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  let t1;
  if ($[0] !== selectedGroup) {
    t1 = getSelectedGroupKey(selectedGroup);
    $[0] = selectedGroup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedGroupKey = t1;
  const selectedGroupName = selectedGroup?.name ?? "";
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  if ($[2] !== selectedGroupKey || $[3] !== selectedGroupName) {
    t2 = {
      selectedGroupKey,
      selectedGroupName
    };
    $[2] = selectedGroupKey;
    $[3] = selectedGroupName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    activeIndex,
    debouncedValue,
    inputValue,
    searchCriteria,
    setActiveIndex,
    setSearchState
  } = useCreateWaveGroupSearchState(t2);
  let t3;
  if ($[5] !== debouncedValue || $[6] !== disabled || $[7] !== isOpen) {
    t3 = {
      debouncedValue,
      disabled,
      isOpen
    };
    $[5] = debouncedValue;
    $[6] = disabled;
    $[7] = isOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    isFetching,
    suggestions
  } = useCreateWaveGroupSuggestions(t3);
  let t4;
  if ($[9] !== setActiveIndex) {
    t4 = () => {
      setIsOpen(false);
      setActiveIndex(-1);
    };
    $[9] = setActiveIndex;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const closeSearch = t4;
  let t5;
  if ($[11] !== allowClear || $[12] !== disabled || $[13] !== inputRef || $[14] !== onSelect || $[15] !== setSearchState) {
    t5 = () => {
      if (!allowClear || disabled) {
        return;
      }
      setSearchState({
        selectedGroupKey: null,
        inputValue: "",
        searchCriteria: "",
        activeIndex: -1
      });
      onSelect(null);
      setIsOpen(true);
      inputRef.current?.focus();
    };
    $[11] = allowClear;
    $[12] = disabled;
    $[13] = inputRef;
    $[14] = onSelect;
    $[15] = setSearchState;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const clearSelection = t5;
  let t6;
  if ($[17] !== closeSearch || $[18] !== isOpen) {
    t6 = () => {
      if (!isOpen) {
        return;
      }
      closeSearch();
    };
    $[17] = closeSearch;
    $[18] = isOpen;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useClickAway(wrapperRef, t6);
  let t7;
  if ($[20] !== closeSearch || $[21] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      closeSearch();
    };
    $[20] = closeSearch;
    $[21] = isOpen;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  useKeyPressEvent("Escape", t7);
  let t8;
  if ($[23] !== disabled || $[24] !== setActiveIndex) {
    t8 = () => {
      if (disabled) {
        return;
      }
      setIsOpen(true);
      setActiveIndex(-1);
    };
    $[23] = disabled;
    $[24] = setActiveIndex;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const onInputFocus = t8;
  let t9;
  if ($[26] !== allowClear || $[27] !== onSelect || $[28] !== selectedGroup || $[29] !== selectedGroupKey || $[30] !== setSearchState) {
    t9 = value => {
      setSearchState({
        selectedGroupKey: selectedGroup && allowClear ? null : selectedGroupKey,
        inputValue: value,
        searchCriteria: value,
        activeIndex: -1
      });
      setIsOpen(true);
      if (selectedGroup && allowClear) {
        onSelect(null);
      }
    };
    $[26] = allowClear;
    $[27] = onSelect;
    $[28] = selectedGroup;
    $[29] = selectedGroupKey;
    $[30] = setSearchState;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const handleInputChange = t9;
  let t10;
  if ($[32] !== closeSearch || $[33] !== inputRef || $[34] !== onSelect || $[35] !== selectedGroupKey || $[36] !== setSearchState) {
    t10 = group => {
      setSearchState({
        selectedGroupKey,
        inputValue: group.name,
        searchCriteria: group.name,
        activeIndex: -1
      });
      onSelect(group);
      closeSearch();
      inputRef.current?.focus();
    };
    $[32] = closeSearch;
    $[33] = inputRef;
    $[34] = onSelect;
    $[35] = selectedGroupKey;
    $[36] = setSearchState;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const onOptionSelect = t10;
  let t11;
  if ($[38] !== activeIndex || $[39] !== closeSearch || $[40] !== isOpen || $[41] !== onOptionSelect || $[42] !== setActiveIndex || $[43] !== suggestions) {
    t11 = {
      activeIndex,
      closeSearch,
      isOpen,
      onOptionSelect,
      setActiveIndex,
      setIsOpen,
      suggestions
    };
    $[38] = activeIndex;
    $[39] = closeSearch;
    $[40] = isOpen;
    $[41] = onOptionSelect;
    $[42] = setActiveIndex;
    $[43] = suggestions;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  const handleKeyDown = useCreateWaveGroupKeyboardNavigation(t11);
  let t12;
  if ($[45] !== inputValue) {
    t12 = inputValue.trim();
    $[45] = inputValue;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const hasValue = t12.length > 0;
  const helperText = selectedGroup ? `Selected: ${selectedGroup.name}` : `Default: ${defaultLabel}`;
  const showClearButton = allowClear && (hasValue || !!selectedGroup) && !disabled;
  const showNoResults = !isFetching && isOpen && suggestions.length === 0;
  const activeOptionId = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
  let t13;
  if ($[47] !== activeIndex || $[48] !== activeOptionId || $[49] !== clearSelection || $[50] !== handleInputChange || $[51] !== handleKeyDown || $[52] !== hasValue || $[53] !== helperText || $[54] !== inputId || $[55] !== inputValue || $[56] !== isFetching || $[57] !== isOpen || $[58] !== listboxId || $[59] !== onInputFocus || $[60] !== onOptionSelect || $[61] !== searchCriteria || $[62] !== setActiveIndex || $[63] !== showClearButton || $[64] !== showNoResults || $[65] !== suggestions) {
    t13 = {
      activeIndex,
      activeOptionId,
      clearSelection,
      handleInputChange,
      handleKeyDown,
      hasValue,
      helperText,
      inputId,
      inputValue,
      isFetching,
      isOpen,
      listboxId,
      onInputFocus,
      onOptionSelect,
      searchCriteria,
      setActiveIndex,
      showClearButton,
      showNoResults,
      suggestions
    };
    $[47] = activeIndex;
    $[48] = activeOptionId;
    $[49] = clearSelection;
    $[50] = handleInputChange;
    $[51] = handleKeyDown;
    $[52] = hasValue;
    $[53] = helperText;
    $[54] = inputId;
    $[55] = inputValue;
    $[56] = isFetching;
    $[57] = isOpen;
    $[58] = listboxId;
    $[59] = onInputFocus;
    $[60] = onOptionSelect;
    $[61] = searchCriteria;
    $[62] = setActiveIndex;
    $[63] = showClearButton;
    $[64] = showNoResults;
    $[65] = suggestions;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  return t13;
}